import { Button, DialogContent, DialogTitle, ModalClose, ModalDialog } from "@mui/joy";
import { ApiZone, ClimbType } from "griptonite-api-client";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import {
  useCreateZoneMutation,
  useUpdateZoneMutation,
  useDeleteZoneMutation,
} from "@/hooks/routes";

import FormErrorAlert from "../FormErrorAlert";
import TextInput from "../forms/TextInput";
import { useModal } from "./useModal";

const Schema = z.object({
  name: z.string().min(1).max(255),
});
type Schema = z.infer<typeof Schema>;

const EditZone = ({ zone, climbType }: { zone: ApiZone | null; climbType?: ClimbType }) => {
  climbType = zone?.climbType || climbType;

  const zoneDisplayName = climbType === "SPORT" ? "Sector" : "Zone";
  const { close } = useModal();

  const methods = useForm<Schema>({
    defaultValues: {
      name: zone?.name || "",
    },
  });
  const formState = methods.formState;

  const createMutation = useCreateZoneMutation();
  const updateMutation = useUpdateZoneMutation();
  const deleteMutation = useDeleteZoneMutation();

  const onSubmit = async (values: { name: string }) => {
    if (zone) {
      await updateMutation.mutateAsync({ uuid: zone.uuid, ...values });
    } else {
      if (!climbType) {
        // should never happen
        throw new Error();
      }
      await createMutation.mutateAsync({ ...values, climbType });
    }
    close();
  };

  const onDelete = async () => {
    if (zone) {
      try {
        await deleteMutation.mutateAsync(zone.uuid);
        close();
      } catch (e) {
        alert("An unexpected error occurred. Please try again or contact support.");
      }
    }
  };

  return (
    <ModalDialog>
      <ModalClose />
      <DialogTitle>{zone ? `Edit ${zoneDisplayName}` : `Create ${zoneDisplayName}`}</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <FormErrorAlert />
            <TextInput name="name" label="Name" />

            <Button
              sx={{ width: "100%", mt: 1 }}
              type="submit"
              loading={formState.isSubmitting}
              loadingPosition="start"
            >
              {zone ? "Save" : "Create"}
            </Button>
            {zone ? (
              <Button
                color="danger"
                sx={{ width: "100%", mt: 1 }}
                loading={deleteMutation.isPending}
                loadingPosition="start"
                onClick={onDelete}
              >
                Delete
              </Button>
            ) : null}
          </form>
        </FormProvider>
      </DialogContent>
    </ModalDialog>
  );
};

export default EditZone;
